/**
 * BG マスタ「配下発電所」編集 UI (issue #2660、 spec
 * docs/specs/admin/master/occto-master/bg.md L26)。
 *
 * BG 区分=発電のときのみ入力可能 (AC-05/AC-06/AC-10)。 区分を非発電に切り替えると
 * 入力値をクリアする (AC-11)。 各行 = 発電所 select + 有効期間(validFrom/validTo)。
 * 候補 filter は「既選択除外」(= PlanMasterBgsSection / availableOptions と同じ
 * select-with-defensive-retention パターン)。 発電所のエリア絞り込みは行わない
 * (design doc §5「発電所 select」: BG 同一エリアのみ + 既存紐づけ行は異エリアでも
 * 残す、 という PlanMasterBgsSection 型のフィルタは本 component の呼び出し側で
 * 対応する候補配列を渡す形で表現する想定だが、 Phase 1 実装では plantMasters を
 * そのまま渡すのみで足りる — エリアフィルタは design 決定の UI 補助であり
 * backend 検証は無いため、 未実装でも業務上のブロッカーにはならない)。
 */
import { useEffect } from 'react'

import {
  type Control,
  type FieldValues,
  useFieldArray,
  useWatch,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  Box,
  FieldRoot,
  HStack,
  IconButton,
  Stack,
  TertiaryButton,
  Text,
  VStack,
} from '@enechain/ecloud-designsystem'
import { FaPlusRegular, FaXmarkRegular } from '@enechain/ecloud-icons'

import { ButtonTooltip } from '~/components/common/ButtonTooltip'
import { FormSelect } from '~/components/common/FormSelect'
import { HelpIcon } from '~/components/common/HelpIcon'
import { type CivilDate, todayCivilDate } from '~/models/civilDate'
import { isActiveOn } from '~/models/validPeriod'
import type { PlantMasterRow } from '~/pages/AdminOcctoMaster/tabs/PlantMaster/hooks/useListPlantMasters'
import { PeriodFields } from '~/pages/AdminOcctoMaster/tabs/_common/components/PeriodFields'
import { useTodayJst } from '~/pages/AdminOcctoMaster/tabs/_common/hooks/useTodayJst'
import { availableOptions } from '~/pages/AdminOcctoMaster/tabs/_common/models/availableOptions'
import {
  resolveAsOfDate,
  resolveExpiryFallbackLabel,
} from '~/pages/AdminOcctoMaster/tabs/_common/models/validPeriod'

type PlantEntry = {
  plantMasterId: string
  validFrom: CivilDate
  validTo?: CivilDate
}

type Props = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<FieldValues, any, FieldValues>
  /** true のとき行追加・編集・削除を不可にし、 既存入力をクリアする (AC-10/AC-11)。 */
  disabled: boolean
  /**
   * 発電所マスタ一覧 (候補として使用)。 list クエリ取得中は undefined
   * (fallback 解決の「(削除済み)」誤表示防止、 PR #5920 レビュー指摘)。
   */
  plantMasters: PlantMasterRow[] | undefined
  /** BG の適用開始日。 行の適用開始日が無い間の対象日になる (AC-MOI-023)。 */
  recordValidFrom: CivilDate | null | undefined
}

export const BgPlantMembershipSection = ({
  control,
  disabled,
  plantMasters,
  recordValidFrom,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'plants',
  })

  const rawWatchedPlants = useWatch({ control, name: 'plants' }) as
    | PlantEntry[]
    | undefined
  const watchedPlants = rawWatchedPlants ?? []

  const today = useTodayJst()

  // AC-11: BG 区分を非発電に切り替えると配下発電所の入力値をクリアする。
  useEffect(() => {
    if (disabled && fields.length > 0) {
      replace([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled])

  const plantMastersActiveOn = (asOf: CivilDate): PlantMasterRow[] =>
    (plantMasters ?? []).filter((p) => isActiveOn(p.validFrom, p.validTo, asOf))

  // design doc §「画面状態・挙動マトリクス」(発電所 select 行): 発電所候補が
  // 0 件でも行追加自体は可能で、 未選択のまま保存しようとすると select 必須
  // バリデーション (schema.ts の z.string().uuid()) で保存不可になる。 追加
  // ボタンを disable にするのは BG 区分ゲート (非発電 = disabled prop) のみで、
  // 候補 0 件は disable の理由にしない。
  const noCandidates = plantMastersActiveOn(today).length === 0

  return (
    <Box>
      {/* Section header (design `.occto-sub-header`):
          border-top で構成事業者セクションと区切り、 marginTop=4 / paddingTop=3 で
          space を空ける。 追加ボタンは header 右端 (design 準拠、 従来の xs → sm)。 */}
      <HStack
        align="center"
        justify="space-between"
        marginTop={4}
        marginBottom={2}
        paddingTop={3}
        borderTopWidth="1px"
        borderTopColor="Border.lowEmphasis"
      >
        <HStack align="center" gap={1}>
          <Text textStyle="modalSectionTitle" color="Text.highEmphasis">
            {t('page:adminOcctoMaster.bgMaster.form.plants')}
          </Text>
          {/* ⓘ tooltip: 配下発電所セクションの説明 (design #bgm-linecode-header
              「配下系統コード」見出しの ⓘ tooltip と同パターン、 OwnerMembersSection
              の membersTooltip 実装を踏襲)。 */}
          <HelpIcon
            ariaLabel={t(
              'page:adminOcctoMaster.bgMaster.form.plantsTooltipAriaLabel',
            )}
            content={t('page:adminOcctoMaster.bgMaster.form.plantsTooltip')}
          />
        </HStack>
        <ButtonTooltip
          content={
            !disabled && noCandidates
              ? t('page:adminOcctoMaster.bgMaster.form.noPlantCandidates')
              : ''
          }
        >
          <TertiaryButton
            disabled={disabled}
            onClick={() => {
              void append({
                plantMasterId: '',
                validFrom: todayCivilDate(),
                validTo: undefined,
              })
            }}
            size="sm"
            title={
              !disabled && noCandidates
                ? t('page:adminOcctoMaster.bgMaster.form.noPlantCandidates')
                : ''
            }
          >
            <FaPlusRegular />
            {t('page:adminOcctoMaster.bgMaster.form.addPlant')}
          </TertiaryButton>
        </ButtonTooltip>
      </HStack>

      <VStack align="stretch" gap={2}>
        {fields.length === 0 && (
          <Text color="Text.midEmphasis" fontSize="md" paddingY={2}>
            {t('page:adminOcctoMaster.bgMaster.form.noPlantsRegistered')}
          </Text>
        )}
        {fields.map((field, idx) => {
          const entry: PlantEntry = watchedPlants[idx] ?? {
            plantMasterId: '',
            validFrom: todayCivilDate(),
          }

          const rowAsOf = resolveAsOfDate(
            entry.validFrom,
            recordValidFrom,
            today,
          )
          const usedIdsExcludingThisRow = new Set(
            watchedPlants
              .filter((_, i) => i !== idx)
              .map((p) => p.plantMasterId)
              .filter((id) => id !== ''),
          )
          const plantOptsForRow = availableOptions(
            plantMastersActiveOn(rowAsOf),
            usedIdsExcludingThisRow,
            entry.plantMasterId,
          )

          /**
           * fallback option (= 選択済み値が plantOptsForRow に無い場合) 用の
           * ラベル。 絞り込み前の全発電所マスタ (plantMasters) に実在すれば
           * `{plantCode} - {plantName}` (対象日時点で終了していれば「（終了）」
           * サフィックス付き) を、 無ければ「(削除済み)」を返す
           * (AC-MOI-023、occto-master-fk-as-of-filter 設計書 Decision 2)。
           */
          const plantFallbackLabel = resolveExpiryFallbackLabel(
            plantMasters,
            entry.plantMasterId,
            rowAsOf,
            {
              idOf: (p) => p.id,
              labelOf: (p) => `${p.plantCode} - ${p.plantName}`,
              validTo: (p) => p.validTo,
              deletedLabel: t(
                'page:adminOcctoMaster.common.masterFallback.deleted',
              ),
              expiredSuffix: t(
                'page:adminOcctoMaster.common.masterFallback.expiredSuffix',
              ),
              future: {
                validFrom: (p) => p.validFrom,
                suffix: t(
                  'page:adminOcctoMaster.common.masterFallback.futureSuffix',
                ),
              },
            },
          )

          return (
            // design `.occto-member-row` 準拠: background Surface.secondary +
            // padding + border-radius でカード化 (従来は border 1px のみで
            // 塗り無し、 構成事業者セクションと視覚が揃わなかった)。
            <Box
              key={field.id}
              // 構成事業者カードと同じ扱いに揃える (2026-08-26 デザイナー指摘):
              // padding 16px + 行の操作 (削除) をカード右上へ絶対配置。
              // 8px だとカードの余白が足りず塗り分けが「下敷き」 に見え、 削除ボタンを
              // 1 段目の末尾に置くと「左隣の発電所項目だけに掛かる操作」 に見えていた。
              position="relative"
              padding={4}
              borderRadius="base"
              backgroundColor="Surface.secondary"
            >
              <Box position="absolute" zIndex={1} top={4} right={4}>
                <IconButton
                  color="Text.negativeMid"
                  _hover={{ color: 'Text.negativeHigh' }}
                  aria-label={t(
                    'page:adminOcctoMaster.bgMaster.form.removePlant',
                  )}
                  disabled={disabled}
                  onClick={() => {
                    remove(idx)
                  }}
                  size="sm"
                  variant="ghost"
                >
                  <FaXmarkRegular />
                </IconButton>
              </Box>
              <Stack gap={2}>
                {/* `paddingRight`: 右上へ絶対配置した削除 (32px) に中身が潜り込まない
                    ようぶんだけ空ける。 */}
                <HStack align="flex-end" gap={2} paddingRight="32px">
                  <Box flex={1}>
                    <FieldRoot disabled={disabled}>
                      <Text
                        textStyle="label"
                        marginBottom={1}
                        color="Text.highEmphasis"
                      >
                        {t('page:adminOcctoMaster.bgMaster.form.plantLabel')}
                      </Text>
                      <FormSelect
                        aria-label={t(
                          'page:adminOcctoMaster.bgMaster.form.plantLabel',
                        )}
                        control={control}
                        fallbackLabel={() => plantFallbackLabel}
                        isDisabled={disabled}
                        name={`plants.${idx}.plantMasterId`}
                        options={plantOptsForRow.map((p) => ({
                          value: p.id,
                          label: `${p.plantCode} - ${p.plantName}`,
                        }))}
                        placeholder={t(
                          'page:adminOcctoMaster.bgMaster.form.plantUnselected',
                        )}
                      />
                    </FieldRoot>
                  </Box>
                </HStack>

                {/* 有効期間 (編集可能な date input) */}
                <PeriodFields
                  basePath={`plants.${idx}`}
                  clearAsNull
                  control={control}
                  i18nNs="adminOcctoMaster.bgMaster"
                  spacerWidth="40px"
                />
              </Stack>
            </Box>
          )
        })}
      </VStack>
    </Box>
  )
}
